import React, { useState, useCallback } from 'react';
import { ChevronRight, Copy, Check } from 'lucide-react';

interface CodeBlockProps {
  children: string;
  title?: string;
}

export function CodeBlock({ children, title = 'Code Example' }: CodeBlockProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback - silently fail
    }
  }, [children]);

  return (
    <div className="mt-4 border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
      >
        <ChevronRight
          className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
        />
        <span>{title}</span>
        {isOpen && (
          <button
            type="button"
            onClick={handleCopy}
            className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {copied ? (
              <>
                <Check className="w-3 h-3" />
                <span>Copied</span>
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" />
                <span>Copy</span>
              </>
            )}
          </button>
        )}
      </button>
      {isOpen && (
        <pre className="px-3 pb-3 bg-muted/30 text-muted-foreground text-xs overflow-x-auto overflow-y-hidden leading-relaxed">
          {children}
        </pre>
      )}
    </div>
  );
}
